"use client"

import { useState } from "react"
import { ProcessedEntry } from "@/types/import"
import { createNoteFromProcessedEntry } from "util/import"

/**
 * Custom hook to manage the acknowledgment step of Mirror imports
 * Handles converting entries to notes format
 *
 * @param entries - The processed entries from Mirror
 * @param onContinue - Callback function when processing is complete
 * @returns Object containing state and functions for the acknowledgment step
 */
export default function useMirrorAcknowledge(
  entries: ProcessedEntry[],
  onContinue: (notes: any[]) => void
) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Processes Mirror entries and converts them to notes format
   */
  const handleContinue = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Convert entries to notes format
      const processedNotes = createNoteFromProcessedEntry(entries)

      // Handle the case where some notes might be filtered out
      if (!processedNotes || processedNotes.length === 0) {
        throw new Error("Failed to process Mirror entries")
      }

      onContinue(processedNotes)
    } catch (e) {
      console.error(e)
      setError("Failed to process Mirror entries")
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    error,
    handleContinue,
  }
}
